import type { Actor } from './actor';
import type { StatusApply } from './skillTypes';
import { addMods, deriveStats, emptyMods } from './stats';
import { getStatus } from './status';
import type { World } from './world';
import { hardCCAllowed } from './combat';

/**
 * Applies a status on its target. Must only be called on the target's owning
 * peer (the world's routing guarantees that).
 */
export function applyStatus(world: World, target: Actor, st: StatusApply, sourceId: number, sourceAttack: number): boolean {
  if (!target.alive) return false;
  const def = getStatus(st.id);
  if (def.hardCC && !hardCCAllowed(target)) return false;
  const existing = target.statuses.find((s) => s.id === st.id);
  const value = st.value ?? 0;
  const stacks = st.stacks ?? 1;
  if (existing) {
    switch (def.stacking) {
      case 'refresh':
        existing.remaining = Math.max(existing.remaining, st.duration);
        existing.duration = Math.max(existing.duration, st.duration);
        existing.value = Math.max(existing.value, value);
        existing.sourceAttack = Math.max(existing.sourceAttack, sourceAttack);
        break;
      case 'stack':
        existing.stacks = Math.min(def.maxStacks ?? 99, existing.stacks + stacks);
        existing.remaining = st.duration;
        existing.duration = st.duration;
        existing.value = Math.max(existing.value, value);
        existing.sourceAttack = Math.max(existing.sourceAttack, sourceAttack);
        break;
      case 'strongest':
        if (value >= existing.value || st.duration > existing.remaining) {
          existing.value = Math.max(existing.value, value);
          existing.remaining = Math.max(existing.remaining, st.duration);
          existing.duration = Math.max(existing.duration, st.duration);
        }
        break;
    }
  } else {
    target.statuses.push({
      id: st.id,
      sourceId,
      sourceAttack,
      remaining: st.duration,
      duration: st.duration,
      stacks: Math.min(def.maxStacks ?? 99, stacks),
      value,
      tickTimer: def.tick?.interval ?? 0,
    });
    world.emit({ type: 'status', tgtId: target.id, statusId: st.id, on: true });
  }
  // Hard CC interrupts whatever the target was doing.
  if (def.cc?.disable || def.cc?.fear) world.interruptCast(target);
  target.statsDirty = true;
  return true;
}

export function removeStatus(world: World, target: Actor, id: string): void {
  const i = target.statuses.findIndex((s) => s.id === id);
  if (i < 0) return;
  target.statuses.splice(i, 1);
  target.statsDirty = true;
  world.emit({ type: 'status', tgtId: target.id, statusId: id, on: false });
}

/** Removes cleansable debuffs (panacea / charms). */
export function cleanse(world: World, target: Actor, max = 99): number {
  let n = 0;
  for (const s of [...target.statuses]) {
    if (n >= max) break;
    const def = getStatus(s.id);
    if (def.kind === 'debuff' && def.cleansable) {
      removeStatus(world, target, s.id);
      n++;
    }
  }
  return n;
}

export function recomputeStats(a: Actor): void {
  const mods = emptyMods();
  for (const s of a.statuses) {
    const def = getStatus(s.id);
    if (def.mods) addMods(mods, def.mods, s.stacks);
    if (def.valueMod) mods[def.valueMod] += s.value;
  }
  a.stats = deriveStats(a.base, mods);
  a.shield = a.statuses.reduce((sum, s) => sum + (s.shield ?? 0), 0);
  a.hp = Math.min(a.hp, a.stats.maxHp);
  a.mp = Math.min(a.mp, a.stats.maxMp);
  a.statsDirty = false;
}

/** Ticks durations, DoTs/HoTs and expiry for actors owned by this peer. */
export function updateStatuses(world: World, a: Actor, dt: number): void {
  for (let i = a.statuses.length - 1; i >= 0; i--) {
    const s = a.statuses[i];
    if (!s) continue;
    const def = getStatus(s.id);
    if (def.tick) {
      s.tickTimer -= dt;
      while (s.tickTimer <= 0 && s.remaining > 0) {
        s.tickTimer += def.tick.interval;
        if (def.tick.kind === 'damage') {
          const src = world.actors.get(s.sourceId) ?? null;
          world.applyDot(a, src, s.sourceAttack * s.value * s.stacks, def.element ?? 'none');
        } else if (def.tick.kind === 'heal') {
          world.heal(a, a.stats.maxHp * s.value, s.sourceId);
        } else {
          a.mp = Math.min(a.stats.maxMp, a.mp + a.stats.maxMp * s.value);
        }
        if (!a.alive) return;
      }
    }
    s.remaining -= dt;
    if (s.remaining <= 0 || (s.id === 'shield' && (s.shield ?? 0) <= 0)) {
      a.statuses.splice(i, 1);
      a.statsDirty = true;
      world.emit({ type: 'status', tgtId: a.id, statusId: s.id, on: false });
      if (def.hardCC && world.pvp && a.isPlayer) {
        // PvP safety net: brief immunity after hard CC.
        applyStatus(world, a, { id: 'debuffImmune', duration: 7 }, a.id, 0);
      }
    }
  }
  if (a.statsDirty) recomputeStats(a);
}
